import { useState } from "react";
import { ClipboardCopy } from "lucide-react";
import { toast } from "sonner";
import {
  SimpleModal,
  SimpleModalHeader,
  SimpleModalTitle,
  SimpleModalDescription,
  SimpleModalFooter,
} from "../../../components/ui/simple-modal";
import {
  validatePollJson,
  validateAddonPollJson,
  VALIDATION_MODE,
  POLL_KIND,
  IMPORT_MODE,
  generateSessionPollSchemaPrompt,
  generateBasicPollSchemaPrompt,
  generateAddonPollSchemaPrompt,
} from "../../../lib/poll-schema";

const DIALOG_CONFIG = {
  [IMPORT_MODE.SESSION]: {
    title: "Import Session Poll",
    description: "Copy the prompt below and send it to any AI chatbot, then paste the result here.",
    promptGenerator: generateSessionPollSchemaPrompt,
  },
  [IMPORT_MODE.BASIC]: {
    title: "Import Basic Poll",
    description: "Copy the prompt below and send it to any AI chatbot, then paste the result here.",
    promptGenerator: generateBasicPollSchemaPrompt,
  },
  [IMPORT_MODE.ADDON]: {
    title: "Import Add-on Polls",
    description: "Copy the prompt below and send it to any AI chatbot, then paste the result here. You can import one or more add-on polls at once.",
    promptGenerator: generateAddonPollSchemaPrompt,
  },
};

function UploadPollJsonDialogContent({ mode, onOpenChange, onSessionImport, onBasicImport, onAddonImport }) {
  const [jsonText, setJsonText] = useState("");
  const [errors, setErrors] = useState([]);
  const [warnings, setWarnings] = useState([]);
  const [bypassWarning, setBypassWarning] = useState(null);

  const config = DIALOG_CONFIG[mode] || DIALOG_CONFIG[IMPORT_MODE.SESSION];

  const handleCopyPrompt = async () => {
    try {
      await navigator.clipboard.writeText(config.promptGenerator());
      toast.success("AI prompt copied to clipboard");
    } catch {
      toast.error("Failed to copy prompt");
    }
  };

  const handleJsonTextChange = (e) => {
    setJsonText(e.target.value);
    if (bypassWarning) setBypassWarning(null);
  };

  const handleImport = () => {
    setErrors([]);
    setWarnings([]);
    setBypassWarning(null);

    let parsed;
    try {
      parsed = JSON.parse(jsonText);
    } catch (err) {
      setErrors([{ path: "", code: "PARSE_ERROR", message: `Invalid JSON: ${err.message}` }]);
      return;
    }

    if (mode === IMPORT_MODE.ADDON) {
      const result = validateAddonPollJson(parsed);
      if (!result.valid) {
        setErrors(result.errors);
        setWarnings(result.warnings);
        return;
      }
      setWarnings(result.warnings);
      onAddonImport?.(result.normalized);
      onOpenChange(false);
      return;
    }

    // BASIC or SESSION mode — use envelope validation.
    const result = validatePollJson(parsed, VALIDATION_MODE.PREFILL);
    if (!result.valid) {
      setErrors(result.errors);
      setWarnings(result.warnings);
      return;
    }
    setWarnings(result.warnings);

    const resultKind = result.normalized.pollKind;

    if (mode === IMPORT_MODE.BASIC) {
      if (resultKind === POLL_KIND.SESSION) {
        // Kind mismatch — offer bypass.
        setBypassWarning({
          message: "This JSON is a SESSION poll, but you opened the basic poll import. You can still import it as a session poll.",
          actionLabel: "Continue as session poll",
          action: () => {
            onSessionImport(result.normalized);
            onOpenChange(false);
          },
        });
        return;
      }
      onBasicImport(result.normalized);
      onOpenChange(false);
      return;
    }

    // SESSION mode.
    if (resultKind === POLL_KIND.BASIC) {
      setBypassWarning({
        message: "This JSON is a BASIC poll, but you opened the session poll import. You can still import it as a basic poll.",
        actionLabel: "Continue as basic poll",
        action: () => {
          onBasicImport(result.normalized);
          onOpenChange(false);
        },
      });
      return;
    }
    onSessionImport(result.normalized);
    onOpenChange(false);
  };

  return (
    <>
      <SimpleModalHeader>
        <SimpleModalTitle>{config.title}</SimpleModalTitle>
        <SimpleModalDescription>{config.description}</SimpleModalDescription>
      </SimpleModalHeader>

      <div className="mt-4 space-y-4">
        <button
          type="button"
          onClick={handleCopyPrompt}
          className="flex items-center gap-2 rounded-full border border-slate-200 px-3 py-1.5 text-xs font-semibold text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
        >
          <ClipboardCopy className="h-3.5 w-3.5" />
          Copy AI Prompt
        </button>

        <textarea
          rows={12}
          value={jsonText}
          onChange={handleJsonTextChange}
          placeholder='Paste AI-generated JSON here...'
          className="w-full rounded-2xl border border-slate-200 px-4 py-3 font-mono text-xs text-slate-800 placeholder:text-slate-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
        />

        {errors.length > 0 && (
          <div className="space-y-1" data-testid="import-errors">
            {errors.map((err, i) => (
              <p key={i} className="text-xs text-red-600 dark:text-red-400">
                {err.path ? `${err.path}: ` : ""}{err.message}
              </p>
            ))}
          </div>
        )}

        {warnings.length > 0 && (
          <div className="space-y-1" data-testid="import-warnings">
            {warnings.map((warn, i) => (
              <p key={i} className="text-xs text-amber-600 dark:text-amber-400">
                {warn.path ? `${warn.path}: ` : ""}{warn.message}
              </p>
            ))}
          </div>
        )}

        {bypassWarning && (
          <div
            className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 dark:border-amber-700/70 dark:bg-amber-900/30"
            data-testid="import-bypass-warning"
          >
            <p className="text-xs text-amber-700 dark:text-amber-200">{bypassWarning.message}</p>
            <div className="mt-2 flex items-center gap-2">
              <button
                type="button"
                onClick={bypassWarning.action}
                className="rounded-full bg-amber-600 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-amber-500"
              >
                {bypassWarning.actionLabel}
              </button>
              <button
                type="button"
                onClick={() => setBypassWarning(null)}
                className="rounded-full border border-amber-300 px-3 py-1 text-xs font-semibold text-amber-700 transition-colors hover:bg-amber-100 dark:border-amber-700 dark:text-amber-200 dark:hover:bg-amber-900/50"
              >
                Dismiss
              </button>
            </div>
          </div>
        )}
      </div>

      <SimpleModalFooter>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          className="rounded-full border border-slate-200 px-4 py-2 text-sm transition-colors hover:bg-slate-50 dark:border-slate-600 dark:hover:bg-slate-700"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleImport}
          disabled={!jsonText.trim()}
          className="rounded-full bg-brand-primary px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-brand-primary/90 disabled:opacity-50"
        >
          Import
        </button>
      </SimpleModalFooter>
    </>
  );
}

export function UploadPollJsonDialog({ open, onOpenChange, mode = IMPORT_MODE.SESSION, onSessionImport, onBasicImport, onAddonImport }) {
  return (
    <SimpleModal open={open} onOpenChange={onOpenChange} contentClassName="max-w-xl">
      <UploadPollJsonDialogContent
        mode={mode}
        onOpenChange={onOpenChange}
        onSessionImport={onSessionImport}
        onBasicImport={onBasicImport}
        onAddonImport={onAddonImport}
      />
    </SimpleModal>
  );
}
